import { AnimationClip, type SkinnedMesh } from 'three'
import { KeyframeBuilder } from './KeyframeBuilder'
import { PoseSnapshotUtil } from './PoseSnapshot'
import { AnimationUtility } from '../../lib/processes/animations-listing/AnimationUtility'
import { type TransformedAnimationClipPair } from '../anim-library/interfaces/TransformedAnimationClipPair'

/** Default canvas length (in seconds) for a newly-created user-authored clip. */
const DEFAULT_CLIP_DURATION_SECONDS = 2.0

/**
 * Coordinates user-authored animation clips: creating empty ones, baking the current
 * skeleton pose at a given playhead time into the active clip, and tagging the result
 * with `source_type: 'user-authored'` so other features (UI, export) can discriminate.
 *
 * Stateless w.r.t. the rest of the app — it operates on data structures the caller
 * already has (a `TransformedAnimationClipPair[]` array, the active skinned mesh,
 * a playhead time). The caller decides where the clip lives and how it's selected.
 */
export class UserClipController {
  /**
   * Returns a new `TransformedAnimationClipPair` containing an empty user-authored
   * clip with the given name. The clip starts with a 2-second nominal duration so
   * the scrubber is usable immediately — `KeyframeBuilder` preserves this duration
   * across subsequent bakes until tracks legitimately extend past it.
   *
   * If `skinned_mesh` is provided, the bind pose is captured at t=0 so the clip
   * plays back as the bind pose rather than as undefined uninitialised bones.
   */
  static create_empty_user_clip (name: string, skinned_mesh?: SkinnedMesh, position_bone_names: string[] = []): TransformedAnimationClipPair {
    let clip: AnimationClip = KeyframeBuilder.create_empty_clip(name)
    clip.duration = DEFAULT_CLIP_DURATION_SECONDS

    if (skinned_mesh !== undefined) {
      const initial_pose = PoseSnapshotUtil.capture(skinned_mesh.skeleton, 0, { position_bone_names })
      clip = KeyframeBuilder.upsert_pose_keyframe(clip, initial_pose)
    }

    const display_clip = AnimationUtility.deep_clone_animation_clip(clip)
    display_clip.duration = clip.duration

    return {
      original_animation_clip: clip,
      display_animation_clip: display_clip,
      metadata: {
        source_type: 'user-authored',
        tags: []
      }
    }
  }

  /**
   * Bakes the current bone pose of the given skinned mesh's skeleton into the active
   * clip at the given playhead time. Mutates `pair.display_animation_clip` and
   * `pair.original_animation_clip` to a new clip with the keyframe upserted.
   *
   * Returns true if the bake happened, false if the pair is not user-authored
   * (callers should disable the bake button in that case, but defense-in-depth).
   */
  static bake_pose_at_time (
    pair: TransformedAnimationClipPair,
    skinned_mesh: SkinnedMesh,
    time: number,
    position_bone_names: string[] = []
  ): boolean {
    if (pair.metadata.source_type !== 'user-authored') return false

    const snapshot = PoseSnapshotUtil.capture(skinned_mesh.skeleton, time, {
      position_bone_names
    })
    const next = KeyframeBuilder.upsert_pose_keyframe(pair.display_animation_clip, snapshot)
    pair.display_animation_clip = next
    pair.original_animation_clip = AnimationUtility.deep_clone_animation_clip(next)
    return true
  }

  /**
   * Renames a user-authored clip, keeping the display and canonical clips in sync.
   * Returns false for non-user-authored clips or blank names.
   */
  static rename_user_clip (pair: TransformedAnimationClipPair, new_name: string): boolean {
    if (pair.metadata.source_type !== 'user-authored') return false

    const trimmed_name = new_name.trim()
    if (trimmed_name === '') return false

    pair.display_animation_clip = KeyframeBuilder.rename_clip(pair.display_animation_clip, trimmed_name)
    pair.original_animation_clip = KeyframeBuilder.rename_clip(pair.original_animation_clip, trimmed_name)
    return true
  }

  /**
   * Duplicates an editable clip as another user-authored clip with independent
   * AnimationClip instances. Returns null for non-user-authored clips.
   */
  static duplicate_user_clip (pair: TransformedAnimationClipPair, new_name: string): TransformedAnimationClipPair | null {
    if (pair.metadata.source_type !== 'user-authored') return null

    const trimmed_name = new_name.trim()
    if (trimmed_name === '') return null

    const original_clip = AnimationUtility.deep_clone_animation_clip(pair.original_animation_clip)
    const display_clip = AnimationUtility.deep_clone_animation_clip(pair.display_animation_clip)

    return {
      original_animation_clip: KeyframeBuilder.rename_clip(original_clip, trimmed_name),
      display_animation_clip: KeyframeBuilder.rename_clip(display_clip, trimmed_name),
      metadata: {
        ...pair.metadata,
        source_type: 'user-authored',
        tags: [...pair.metadata.tags]
      }
    }
  }

  /**
   * Returns a unique clip name like `My Clip 1`, `My Clip 2`, ... avoiding collisions
   * with any clip in `existing_pairs`.
   */
  static next_unique_clip_name (existing_pairs: TransformedAnimationClipPair[]): string {
    const taken = new Set(existing_pairs.map(p => p.display_animation_clip.name))
    for (let i = 1; i < 1000; i++) {
      const candidate = `My Clip ${i}`
      if (!taken.has(candidate)) return candidate
    }
    return `My Clip ${Date.now()}`
  }

  static next_unique_duplicate_name (base_name: string, existing_pairs: TransformedAnimationClipPair[]): string {
    const taken = new Set(existing_pairs.map(p => p.display_animation_clip.name))
    const clean_base_name = base_name.trim() === '' ? 'My Clip' : base_name.trim()
    for (let i = 1; i < 1000; i++) {
      const candidate = `${clean_base_name} Copy ${i}`
      if (!taken.has(candidate)) return candidate
    }
    return `${clean_base_name} Copy ${Date.now()}`
  }
}
